import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

@customElement('chips-field')
export class ChipsField extends LitElement {
  @property({ type: String }) name = '';
  @property({ type: String }) label = '';
  @property({ type: String }) placeholder = '';
  @property({ type: Boolean }) required = false;
  @property({ type: String }) error = '';
  @property({ type: Boolean }) touched = false;

  @state() private _inputValue = '';

  @property({ type: Array }) value: string[] = [];

  static styles = css`
    :host {
      display: block;
    }
    .field-group {
      margin-bottom: 16px;
    }
    label {
      display: block;
      margin-bottom: 8px;
      font-weight: 500;
      color: #1e293b;
      font-size: 14px;
    }
    .required {
      color: #ef4444;
    }
    .chips-container {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      padding: 8px;
      border: 1px solid #cbd5e1;
      border-radius: 6px;
      min-height: 42px;
      background: white;
    }
    .chips-container input[type='text'] {
      background-color: transparent;
      color: black;
    }
    .chips-container:focus-within {
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
    .chip {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 4px 10px;
      background: #3b82f6;
      color: white;
      border-radius: 16px;
      font-size: 13px;
      animation: chipIn 0.2s ease-out;
    }
    @keyframes chipIn {
      from {
        opacity: 0;
        transform: scale(0.8);
      }
      to {
        opacity: 1;
        transform: scale(1);
      }
    }
    .chip-remove {
      cursor: pointer;
      padding: 0 4px;
      font-size: 16px;
      line-height: 1;
      transition: opacity 0.2s;
    }
    .chip-remove:hover {
      opacity: 0.7;
    }
    input {
      flex: 1;
      min-width: 120px;
      border: none;
      outline: none;
      padding: 4px;
      font-size: 14px;
    }
    .error-message {
      color: #ef4444;
      font-size: 12px;
      margin-top: 4px;
    }
  `;

  private _handleKeyDown(e: KeyboardEvent) {
    if (e.key === 'Enter' && this._inputValue.trim()) {
      e.preventDefault();
      this._addChip(this._inputValue.trim());
    }
  }

  private _addChip(value: string) {
    if (!this.value.includes(value)) {
      this.value = [...this.value, value];
      this._inputValue = '';
      this._emitChange();
    }
  }

  private _removeChip(value: string) {
    this.value = this.value.filter((v) => v !== value);
    this._emitChange();
  }

  private _handleInput(e: Event) {
    const input = e.target as HTMLInputElement;
    this._inputValue = input.value;
  }

  private _handleBlur() {
    this.dispatchEvent(
      new CustomEvent('field-blur', {
        detail: { name: this.name },
        bubbles: true,
        composed: true,
      })
    );
  }

  private _emitChange() {
    this.dispatchEvent(
      new CustomEvent('field-change', {
        detail: { name: this.name, value: this.value },
        bubbles: true,
        composed: true,
      })
    );
  }

  render() {
    return html`
      <div class="field-group">
        <label>
          ${this.label}
          ${this.required ? html`<span class="required">*</span>` : ''}
        </label>
        <div class="chips-container">
          ${this.value.map(
            (chip) => html`
              <span class="chip">
                ${chip}
                <span class="chip-remove" @click=${() => this._removeChip(chip)}
                  >×</span
                >
              </span>
            `
          )}
          <input
            type="text"
            id=${this.name}
            .value=${this._inputValue}
            placeholder=${this.placeholder || ''}
            @input=${this._handleInput}
            @keydown=${this._handleKeyDown}
            @blur=${this._handleBlur} />
        </div>
        ${this.error && this.touched
          ? html` <div class="error-message">${this.error}</div> `
          : ''}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'chips-field': ChipsField;
  }
}
